import { useToast } from '@airship-platform/airship-patterns';
import {
  DescriptionEditable,
  LinkedItems,
  ManageableProduct,
  ManageProductSection,
  ManageProductTechnicianContent,
  useManageProducts,
  useProducts,
} from '@airship-platform/airship-proposal';
import { isItemGroupChild } from '@airship-platform/appointments-feature/utils';
import {
  EmptyState,
  FlexBox,
  Loader,
} from '@airship-platform/jam-web/components';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import {
  AppointmentProposalOptionProduct,
  useAppointmentProposalOptionProducts,
} from '../../data-access/appointmentProposalOptionProducts';
import { useAppointmentProposalOptions } from '../../data-access/appointmentProposalOptions';
import { useAppointments } from '../../data-access/appointments';
import { useDurationBilling } from '../../data-offline/features/membershipData/useDurationBilling';
import { describeOfflineSyncError } from '../../patterns';
import ROUTE_PATHS from '../../routes/paths';
import {
  SubItemsSection,
  useItemGroupMutations,
} from '../proposal-item-groups';
import { RemoveProductModal } from '../proposal-option-items';
import { useOptionCard } from '../proposal-options-buider/hooks/useOptionCard';

const EditProductPage: React.FC = () => {
  const { state, removeProduct, showRemoveProduct } = useOptionCard();
  const { selectedProducts, updateProduct, setProducts } = useManageProducts();
  const {
    products,
    isLoading: isLoadingProducts,
    isLoaded: isLoadedProducts,
    fetchProduct,
  } = useProducts();
  const { currentAppointment } = useAppointments();
  const { currentOption } = useAppointmentProposalOptions();
  const {
    proposalProducts,
    isLoading,
    updateAppointmentProposalOptionProduct,
  } = useAppointmentProposalOptionProducts();
  const { deleteProductCascading } = useItemGroupMutations();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const { appointmentProposalOptionProductId } = useParams<{
    appointmentProposalOptionProductId: string;
  }>();

  const proposalProductsFiltered = useMemo(
    () =>
      proposalProducts.filter(
        (optionProduct) =>
          optionProduct.appointmentProposalOptionProductId ===
          appointmentProposalOptionProductId
      ),
    [proposalProducts, appointmentProposalOptionProductId]
  );
  const pricebookId = proposalProductsFiltered.at(0)?.pricebookId;
  const pricebookEntityType = proposalProductsFiltered.at(0)?.type;
  const durationBillingItems = useDurationBilling(pricebookId ?? '');
  const defaultDurationBillingId = durationBillingItems.filter(
    (item) => item.active !== false
  )[0]?.durationBillingId;

  useEffect(() => {
    if (pricebookId && pricebookEntityType) {
      fetchProduct({ pricebookId, type: pricebookEntityType });
    }
  }, [fetchProduct, pricebookEntityType, pricebookId]);

  const pricebookProduct = products.find(
    (product) => product.pricebookId === pricebookId
  );

  useEffect(() => {
    if (pricebookProduct?.prices) {
      setProducts(
        proposalProductsFiltered.map((product) => ({
          ...product,
          durationBillingId:
            product.durationBillingId ?? defaultDurationBillingId,
          linkedItems: product.linkedItems.map((item) => ({
            ...item,
            categoryIds: item.categoryIds || [],
          })),
          prices:
            product.defaultPriceType === 'MANUAL'
              ? [
                  ...pricebookProduct.prices,
                  {
                    priceType: 'MANUAL' as const,
                    value: product.price,
                    currency: product.currency,
                    taxable: product.taxable,
                  },
                ]
              : pricebookProduct.prices.map((p) =>
                  p.priceType === product.defaultPriceType
                    ? {
                        priceType: product.defaultPriceType,
                        value: product.price,
                        currency: product.currency,
                        taxable: product.taxable,
                      }
                    : p
                ),
        }))
      );
    }

    return () => {
      setProducts([]);
    };
  }, [
    pricebookProduct?.prices,
    proposalProductsFiltered,
    setProducts,
    defaultDurationBillingId,
  ]);

  if (isLoadingProducts && !isLoadedProducts) {
    return <Loader message="Loading Product" isLoading={isLoadingProducts} />;
  }

  if (
    !currentOption ||
    !selectedProducts.length ||
    !appointmentProposalOptionProductId
  ) {
    return (
      <EmptyState title="No option or product selected" subtitle="">
        <Button component={Link} to={'../'}>
          Go back to products
        </Button>
      </EmptyState>
    );
  }

  const isReadOnly = currentOption.isReadOnly ?? false;
  const optionProduct = proposalProductsFiltered.at(0);

  const resetAndNavigate = () => {
    setProducts([]);
    navigate(ROUTE_PATHS.PROPOSAL_OPTIONS_PATH(currentOption));
  };

  const handleEditProduct = () => {
    const productsNames = selectedProducts.map((u) => u.name).join(', ');

    return Promise.all(
      selectedProducts.map((product) =>
        updateAppointmentProposalOptionProduct(
          currentOption.appointmentId,
          currentOption.appointmentProposalId,
          currentOption.appointmentProposalOptionId,
          appointmentProposalOptionProductId,
          {
            description: product.description,
            price: product.price,
            quantity: product.quantity,
            defaultPriceType: product.defaultPriceType,
            durationBillingId: product.durationBillingId,
          }
        )
      )
    ).then(() => {
      showToast(`${productsNames} updated`, 'success');
      resetAndNavigate();
    });
  };

  const onUpdate =
    (productId: string) => (product: Partial<ManageableProduct>) =>
      updateProduct(productId, product);

  const renderRemovalModal = () => {
    const product = proposalProducts.find(
      (product) =>
        product.appointmentProposalOptionProductId === state.currentProductId
    );

    return (
      product && (
        <RemoveProductModal
          product={product}
          isModalOpen={state.isRemoveProductModalOpen}
          isLoading={isLoading}
          onModalClose={() => showRemoveProduct(null)}
          onProductRemove={() => handleProductDelete(product)}
        />
      )
    );
  };

  const handleProductDelete = (product: AppointmentProposalOptionProduct) => {
    deleteProductCascading(product).then(() => {
      showToast('Product removed', 'success');
      removeProduct();
      resetAndNavigate();
    });
  };

  return (
    <FlexBox width={'100%'} flexDirection={'column'} p={[2, 4]} gap={3}>
      <Typography variant="h4">Edit item</Typography>

      {selectedProducts.map((product) => (
        <ManageProductSection
          key={product.pricebookId}
          product={{
            ...product,
            error: describeOfflineSyncError(product.offlineSyncErrors),
          }}
        >
          <ManageProductTechnicianContent
            product={product}
            hasMembership={!!currentAppointment?.memberships?.length}
            isLoading={isLoading}
            isDisabled={isReadOnly}
            submitButtonText="Update"
            durationBillingItems={durationBillingItems}
            onUpdateProduct={onUpdate(product.pricebookId)}
            onCancel={resetAndNavigate}
            onSubmit={handleEditProduct}
            onDelete={() =>
              showRemoveProduct(appointmentProposalOptionProductId)
            }
          />

          <Divider flexItem />

          <DescriptionEditable
            description={product.description}
            onUpdateDescription={(newDescription) =>
              onUpdate(product.pricebookId)({
                description: newDescription,
              })
            }
            isEditable={!isReadOnly}
          />

          {!!product.linkedItems.length && (
            <>
              <Divider flexItem sx={{ my: 2 }} />
              <LinkedItems
                linkedItems={product.linkedItems}
                products={proposalProducts.filter(
                  (p) =>
                    p.appointmentProposalOptionId ===
                    currentOption.appointmentProposalOptionId
                )}
              />
            </>
          )}

          {optionProduct &&
            !isItemGroupChild(optionProduct) &&
            optionProduct.groupingName !== 'LINKED_ITEM' && (
              <>
                <Divider flexItem sx={{ my: 2 }} />
                <SubItemsSection
                  rootProduct={optionProduct}
                  isReadOnly={isReadOnly}
                />
              </>
            )}
        </ManageProductSection>
      ))}

      {renderRemovalModal()}
    </FlexBox>
  );
};

export default EditProductPage;
